import React from "react";
import { Button, Tag } from "antd";
import { ColumnType } from "antd/lib/table";
import moment from "moment";
import { DATETIME_FORMAT, EMPTY } from "constants/common";
import { AICC_PROMPT_SOURCE_COLOR, AICC_PROMPT_SOURCE_LABEL } from "constants/aiccPrompt";
import { AiccPromptModel, AiccPromptSummary } from "types/model/aiccPrompt";
import { isModelRetired, modelAvailability } from "../PromptDetail/ModelSettings/availability";
import styles from "./PromptList.module.scss";

interface Options {
  modelsById: Record<string, AiccPromptModel>;
  isEditable: boolean;
  onConfigure: (prompt: AiccPromptSummary) => void;
}

const getColumns = ({ modelsById, isEditable, onConfigure }: Options): ColumnType<AiccPromptSummary>[] => [
  {
    title: "Prompt Name",
    dataIndex: "name",
    key: "name",
    width: 320,
    render: (_, { name, description }) => (
      <div>
        <div className={styles.promptName}>{name || EMPTY}</div>
        {!!description && description !== name && <div className={styles.promptDescription}>{description}</div>}
      </div>
    ),
  },
  {
    title: "Status",
    dataIndex: "source",
    key: "source",
    width: 180,
    render: (_, { source }) => (
      <div className={styles.chipCell}>
        <Tag color={AICC_PROMPT_SOURCE_COLOR[source]}>{AICC_PROMPT_SOURCE_LABEL[source]}</Tag>
      </div>
    ),
  },
  {
    title: "Current Model",
    dataIndex: "modelId",
    key: "modelId",
    width: 260,
    /**
     * 🔴 THE EFFORT SITS BESIDE THE TAG, NOT UNDER IT. Under it the cell becomes two lines, and since the
     * cells here are centred (`.table`) that lifts this chip above the row's centre — which is precisely the
     * misalignment against the Status chip that `ee21f581` measured and fixed. Hence one line, and the column
     * widened from 200 to fit both without wrapping.
     */
    render: (_, { modelId, reasoningEffort }) => {
      const model = modelId ? modelsById[modelId] : undefined;
      const availability = modelAvailability({ ...model }, { compact: true });
      const retired = isModelRetired({ ...model });

      return (
        <div className={styles.chipCell}>
          {/* Uncoloured, including when the model is retiring: every row in this column has a model, so a
              fill on each is noise. The retirement note beside it is what carries that. */}
          <Tag>{(modelId && (model?.name || modelId)) || EMPTY}</Tag>
          {/* 🔴 An id the catalogue does not list is shown as itself and NOT called retiring: unknown is not
              the same claim, and this grid is where an operator would act on it.
              The compact wording, since the cell holds the name and the effort too — `modelAvailability`
              owns both lengths so this cell and the detail page cannot describe one model two ways. */}
          {!!availability && (
            <span className={retired ? styles.retiredLabel : styles.availabilityLabel}>{availability}</span>
          )}
          {!!reasoningEffort && <span className={styles.effort}>{`Effort: ${reasoningEffort}`}</span>}
        </div>
      );
    },
  },
  {
    title: "Last Modified",
    dataIndex: "lastModifiedAt",
    key: "lastModifiedAt",
    width: 220,
    render: (_, { lastModifiedBy, lastModifiedAt }) => (
      <div>
        <div>{lastModifiedBy || EMPTY}</div>
        {!!lastModifiedAt && <div className={styles.timestamp}>{moment(lastModifiedAt).format(DATETIME_FORMAT)}</div>}
      </div>
    ),
  },
  {
    title: "Action",
    dataIndex: "action",
    key: "action",
    width: 140,
    render: (_, prompt) => (
      <Button
        type="link"
        className={styles.linkButton}
        onClick={(event) => {
          event.stopPropagation();
          onConfigure(prompt);
        }}
      >
        {isEditable ? "Configure" : "View"}
      </Button>
    ),
  },
];

export default getColumns;
